// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { StyledOverlay } from "@rugby/App/Components/XngMenu/XngMenu.Styles";
import { createUniversalPortal } from "@rugby/App/Components/XngMenu/XngMenu.utils";
import { xCommonState, xHeaderState } from "@rugby/App/Store/Reducers";
import { constants } from "@rugby/XDL/constants";
import { EventBus } from "@rugby/XDL/Utilities";
import React, { useContext, useEffect, useState } from "react";
import { connect } from "react-redux";

const mapStateToProps = (state: xHeaderState & xCommonState) => ({
    location: getLocation(state),
    isSticky: state.xHeader.isSticky,
    tippyVisibility: state.xCommon.tippyVisibility,
});

interface CustomEvent extends MouseEvent {
    target: Node;
}

export const XngMenusContext = React.createContext({
    showMenu: (menuId: string, data?: any) => {},
    hideMenu: (menuId: string) => {},
    hideAllMenus: () => {},
    activeMenu: {},
    activeMenus: {},
});

export const XngMenusProvider = connect(mapStateToProps)(({ tippyVisibility, children, location }: any) => {
    const [activeMenus, setActiveMenus] = useState({});
    const [activeMenu, setActiveMenu] = useState({});
    const [showTimers, setShowTimers] = useState<{ [key: string]: number }>({});
    const [hideTimers, setHideTimers] = useState<{ [key: string]: number }>({});

    // RUG-942 - To solve SSR warning issue
    const [isMounted, setIsMounted] = useState(false);

    const showMenu = (menuId: string, data?: any) => {
        setHideTimers(currentHideTimers => {
            clearTimeout(currentHideTimers[menuId]);
            return currentHideTimers;
        });

        setShowTimers(currentShowTimers => {
            for (const key in currentShowTimers) {
                clearTimeout(currentShowTimers[key]);
            }
            return {
                ...currentShowTimers,
                [menuId]: setTimeout(() => {
                    setActiveMenus((prevActiveMenus: { [key: string]: any }) => {
                        const newActiveMenus = removeMenuChilds(prevActiveMenus, data.parentId);
                        return { ...newActiveMenus, [menuId]: data };
                    });
                    setActiveMenu(data);
                }, 350),
            };
        });
    };

    const hideMenu = (menuId: string) => {
        setShowTimers(currentShowTimers => {
            clearTimeout(currentShowTimers[menuId]);
            return currentShowTimers;
        });

        setHideTimers(currentHideTimers => {
            clearTimeout(currentHideTimers[menuId]);

            return {
                ...currentHideTimers,
                [menuId]: setTimeout(() => {
                    setActiveMenus((prevActiveMenus: { [key: string]: any }) => {
                        const newActiveMenus = removeMenuChilds(prevActiveMenus, menuId);
                        delete newActiveMenus[menuId];
                        return newActiveMenus;
                    });
                }, 350),
            };
        });
    };

    const removeMenuChilds = (activeMenus: { [key: string]: any }, parentId: string) => {
        const openedMenus = Object.values(activeMenus);
        const newActiveMenus: { [key: string]: any } = openedMenus.reduce((currentObj, openedMenu) => {
            if (openedMenu.parentId.indexOf(parentId) === -1 || openedMenu.menuId === parentId) {
                currentObj[openedMenu.menuId] = openedMenu;
            }
            return currentObj;
        }, {});
        return newActiveMenus;
    };

    const hideAllMenus = () => {
        setActiveMenus({});
        setActiveMenu({});
        setShowTimers(currentShowTimers => {
            for (const key in currentShowTimers) {
                clearTimeout(currentShowTimers[key]);
            }
            return {};
        });

        setHideTimers({});
    };

    useEffect(() => {
        hideAllMenus();
    }, [location]);

    useEffect(() => {
        // RUG-942 - To solve SSR warning issue
        setIsMounted(true);

        EventBus.on(constants.eventBus.mainNavigation.key, (data: any) => {
            if (data.type === constants.eventBus.mainNavigation.types.hideMenu) {
                hideAllMenus();
            }
        });
        return () => {
            EventBus.remove(constants.eventBus.mainNavigation.key);
        };
    }, []);

    // RUG-942 - To solve SSR warning issue
    if (!isMounted) {
        return <></>;
    }

    return (
        <XngMenusContext.Provider value={{ showMenu, hideMenu, activeMenus, activeMenu, hideAllMenus }}>
            {children}
            {createUniversalPortal(
                <StyledOverlay
                    onClick={() => hideAllMenus()}
                    isVisible={(activeMenu as any)?.withOverlay && Object.keys(activeMenus).length}
                />,
            )}
            {createUniversalPortal(
                <StyledOverlay
                    className="tippyVisibility_overlay"
                    onClick={() => hideAllMenus()}
                    isVisible={tippyVisibility !== "hidden"}
                />,
            )}
        </XngMenusContext.Provider>
    );
});
